import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';

interface WorkflowState {
  heroImagePath?: string;
  nicheImagePaths?: string[];
  wordpressPostUrl?: string;
  wordpressPostId?: number;
  blogContent?: {
    title?: string;
    content?: string;
    metaDescription?: string;
    excerpt?: string;
  };
  podcastAudioUrl?: string;
  podcastEmbedCode?: string;
  podbeanEpisodeId?: string;
  podbeanPermalink?: string;
  videoUrl?: string;
  videoRequestId?: string;
  youtubeVideoId?: string;
  youtubeEmbedCode?: string;
  socialMediaContent?: {
    facebook?: { post?: string };
    instagram?: { caption?: string; hashtags?: string[] };
    pinterest?: { title?: string; description?: string };
    twitter?: { tweet?: string };
    linkedin?: { post?: string };
  };
  gmbDescription?: string;
  error?: string;
}

interface WorkflowStep {
  id: string;
  stepNumber: number;
  stepName: string;
  status: string;
  result?: Record<string, unknown>;
  error?: string;
  startedAt?: string;
  completedAt?: string;
}

interface Workflow {
  id: string;
  paaText: string;
  paaSlug: string;
  status: string;
  currentStep: number;
  totalSteps: number;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  errorMessage?: string;
  errorStep?: number;
  client: {
    id: string;
    companyName: string;
    slug: string;
  };
  steps: WorkflowStep[];
  state: WorkflowState;
}

interface WorkflowResponse {
  workflow: Workflow;
}

export function WorkflowDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    loadWorkflow();
    // Poll if processing
    const interval = setInterval(() => {
      if (workflow?.status === 'PROCESSING') {
        loadWorkflow();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [id, workflow?.status]);

  async function loadWorkflow() {
    try {
      const res = await api.get<WorkflowResponse>(`/workflows/${id}`);
      setWorkflow(res.workflow);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workflow');
    } finally {
      setLoading(false);
    }
  }

  function copyToClipboard(text: string, fieldName: string) {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  }

  function getStatusBadge(status: string) {
    const styles: Record<string, string> = {
      COMPLETED: 'bg-green-100 text-green-800',
      PROCESSING: 'bg-blue-100 text-blue-800',
      PAUSED: 'bg-yellow-100 text-yellow-800',
      FAILED: 'bg-red-100 text-red-800',
      PENDING: 'bg-gray-100 text-gray-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  }

  function formatDate(dateString?: string) {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error || !workflow) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error || 'Workflow not found'}</p>
        <Link to="/workflows" className="text-primary-600 hover:underline mt-4 inline-block">
          Back to Workflows
        </Link>
      </div>
    );
  }

  const state = workflow.state || {};

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <Link to="/workflows" className="text-sm text-gray-500 hover:text-gray-700 mb-2 inline-block">
            &larr; Back to Workflows
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{workflow.paaText}</h1>
          <p className="text-gray-600">{workflow.client.companyName}</p>
        </div>
        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadge(workflow.status)}`}>
          {workflow.status}
        </span>
      </div>

      {/* Progress */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Progress</h2>
        <div className="mb-2 flex justify-between text-sm text-gray-600">
          <span>Step {workflow.currentStep} of {workflow.totalSteps}</span>
          <span>{Math.round((workflow.currentStep / workflow.totalSteps) * 100)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all duration-500"
            style={{ width: `${(workflow.currentStep / workflow.totalSteps) * 100}%` }}
          />
        </div>
        {workflow.errorMessage && (
          <p className="mt-3 text-sm text-red-600">{workflow.errorMessage}</p>
        )}
      </div>

      {/* Output Links */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Output Links</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Blog/WordPress */}
          <OutputSection title="Blog Post">
            {state.wordpressPostUrl ? (
              <OutputLink url={state.wordpressPostUrl} label="WordPress Post" />
            ) : (
              <span className="text-gray-400 text-sm">Not yet generated</span>
            )}
            {state.blogContent?.title && (
              <p className="text-sm text-gray-600 mt-1">{state.blogContent.title}</p>
            )}
          </OutputSection>

          {/* Hero Image */}
          <OutputSection title="Hero Image">
            {state.heroImagePath ? (
              <div>
                <p className="text-sm text-gray-600 font-mono break-all">{state.heroImagePath}</p>
                <p className="text-xs text-gray-400 mt-1">Local path on server</p>
              </div>
            ) : (
              <span className="text-gray-400 text-sm">Not yet generated</span>
            )}
          </OutputSection>

          {/* Niche Images */}
          <OutputSection title="Niche Images">
            {state.nicheImagePaths && state.nicheImagePaths.length > 0 ? (
              <div className="space-y-1">
                {state.nicheImagePaths.map((path, i) => (
                  <p key={i} className="text-sm text-gray-600 font-mono break-all">
                    {path}
                  </p>
                ))}
                <p className="text-xs text-gray-400 mt-1">Local paths on server</p>
              </div>
            ) : (
              <span className="text-gray-400 text-sm">Not yet generated</span>
            )}
          </OutputSection>

          {/* Podcast */}
          <OutputSection title="Podcast">
            {state.podcastAudioUrl ? (
              <OutputLink url={state.podcastAudioUrl} label="Audio File" />
            ) : (
              <span className="text-gray-400 text-sm">Not yet generated</span>
            )}
            {state.podbeanPermalink && (
              <OutputLink url={state.podbeanPermalink} label="Podbean Episode" />
            )}
          </OutputSection>

          {/* Podcast Embed */}
          {state.podcastEmbedCode && (
            <OutputSection title="Podcast Embed Code">
              <CopyableText
                text={state.podcastEmbedCode}
                fieldName="podcastEmbed"
                copiedField={copiedField}
                onCopy={copyToClipboard}
              />
            </OutputSection>
          )}

          {/* Video */}
          <OutputSection title="Video">
            {state.videoUrl ? (
              <OutputLink url={state.videoUrl} label="Video File" />
            ) : (
              <span className="text-gray-400 text-sm">Not yet generated</span>
            )}
            {state.youtubeVideoId && (
              <OutputLink
                url={`https://www.youtube.com/watch?v=${state.youtubeVideoId}`}
                label="YouTube Video"
              />
            )}
          </OutputSection>

          {/* YouTube Embed */}
          {state.youtubeEmbedCode && (
            <OutputSection title="YouTube Embed Code">
              <CopyableText
                text={state.youtubeEmbedCode}
                fieldName="youtubeEmbed"
                copiedField={copiedField}
                onCopy={copyToClipboard}
              />
            </OutputSection>
          )}

          {/* GBP Description */}
          {state.gmbDescription && (
            <OutputSection title="GBP Post Description">
              <CopyableText
                text={state.gmbDescription}
                fieldName="gmbDescription"
                copiedField={copiedField}
                onCopy={copyToClipboard}
              />
            </OutputSection>
          )}
        </div>
      </div>

      {/* Social Media Content */}
      {state.socialMediaContent && Object.keys(state.socialMediaContent).length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Social Media Content</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Facebook */}
            {state.socialMediaContent.facebook?.post && (
              <OutputSection title="Facebook Post">
                <CopyableText
                  text={state.socialMediaContent.facebook.post}
                  fieldName="facebook"
                  copiedField={copiedField}
                  onCopy={copyToClipboard}
                />
              </OutputSection>
            )}

            {/* Instagram */}
            {state.socialMediaContent.instagram?.caption && (
              <OutputSection title="Instagram Caption">
                <CopyableText
                  text={state.socialMediaContent.instagram.caption}
                  fieldName="instagram"
                  copiedField={copiedField}
                  onCopy={copyToClipboard}
                />
                {state.socialMediaContent.instagram.hashtags && (
                  <p className="text-sm text-gray-500 mt-2">
                    {state.socialMediaContent.instagram.hashtags.join(' ')}
                  </p>
                )}
              </OutputSection>
            )}

            {/* Pinterest */}
            {state.socialMediaContent.pinterest?.description && (
              <OutputSection title="Pinterest Pin">
                <p className="text-sm font-medium text-gray-700 mb-1">
                  {state.socialMediaContent.pinterest.title}
                </p>
                <CopyableText
                  text={state.socialMediaContent.pinterest.description}
                  fieldName="pinterest"
                  copiedField={copiedField}
                  onCopy={copyToClipboard}
                />
              </OutputSection>
            )}

            {/* Twitter/X */}
            {state.socialMediaContent.twitter?.tweet && (
              <OutputSection title="Twitter/X Post">
                <CopyableText
                  text={state.socialMediaContent.twitter.tweet}
                  fieldName="twitter"
                  copiedField={copiedField}
                  onCopy={copyToClipboard}
                />
              </OutputSection>
            )}

            {/* LinkedIn */}
            {state.socialMediaContent.linkedin?.post && (
              <OutputSection title="LinkedIn Post">
                <CopyableText
                  text={state.socialMediaContent.linkedin.post}
                  fieldName="linkedin"
                  copiedField={copiedField}
                  onCopy={copyToClipboard}
                />
              </OutputSection>
            )}
          </div>
        </div>
      )}

      {/* Workflow Steps */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Workflow Steps</h2>
        <div className="space-y-2">
          {workflow.steps.map((step) => (
            <div
              key={step.id}
              className={`flex items-center justify-between p-3 rounded-lg ${
                step.status === 'COMPLETED' ? 'bg-green-50' :
                step.status === 'PROCESSING' ? 'bg-blue-50' :
                step.status === 'FAILED' ? 'bg-red-50' :
                'bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                  step.status === 'COMPLETED' ? 'bg-green-200 text-green-800' :
                  step.status === 'PROCESSING' ? 'bg-blue-200 text-blue-800' :
                  step.status === 'FAILED' ? 'bg-red-200 text-red-800' :
                  'bg-gray-200 text-gray-600'
                }`}>
                  {step.stepNumber + 1}
                </span>
                <span className="font-medium text-gray-900">{step.stepName}</span>
              </div>
              <div className="flex items-center gap-4">
                {step.completedAt && (
                  <span className="text-xs text-gray-500">
                    {formatDate(step.completedAt)}
                  </span>
                )}
                <span className={`text-xs font-medium ${
                  step.status === 'COMPLETED' ? 'text-green-600' :
                  step.status === 'PROCESSING' ? 'text-blue-600' :
                  step.status === 'FAILED' ? 'text-red-600' :
                  'text-gray-500'
                }`}>
                  {step.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Metadata */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Metadata</h2>
        <dl className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-gray-500">Workflow ID</dt>
            <dd className="font-mono text-gray-900">{workflow.id}</dd>
          </div>
          <div>
            <dt className="text-gray-500">PAA Slug</dt>
            <dd className="font-mono text-gray-900">{workflow.paaSlug}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Created</dt>
            <dd className="text-gray-900">{formatDate(workflow.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Updated</dt>
            <dd className="text-gray-900">{formatDate(workflow.updatedAt)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Started</dt>
            <dd className="text-gray-900">{formatDate(workflow.startedAt)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Completed</dt>
            <dd className="text-gray-900">{formatDate(workflow.completedAt)}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

function OutputSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-medium text-gray-700 mb-2">{title}</h3>
      {children}
    </div>
  );
}

function OutputLink({ url, label }: { url: string; label: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary-600 hover:text-primary-800 text-sm hover:underline flex items-center gap-1"
    >
      {label}
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    </a>
  );
}

function CopyableText({
  text,
  fieldName,
  copiedField,
  onCopy
}: {
  text: string;
  fieldName: string;
  copiedField: string | null;
  onCopy: (text: string, fieldName: string) => void;
}) {
  const isCopied = copiedField === fieldName;

  return (
    <div className="relative">
      <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 max-h-32 overflow-y-auto">
        {text}
      </div>
      <button
        onClick={() => onCopy(text, fieldName)}
        className={`absolute top-2 right-2 px-2 py-1 text-xs rounded ${
          isCopied
            ? 'bg-green-100 text-green-700'
            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
        }`}
      >
        {isCopied ? 'Copied!' : 'Copy'}
      </button>
    </div>
  );
}
